import * as React from "react";

import HelpIcon from "@mui/icons-material/Help";
import { FormControlLabel, IconButton, Paper, Switch, TextField, Tooltip } from "@mui/material";
import { styled } from "@mui/material/styles";
import { FormState, UseFormClearErrors, UseFormRegister, UseFormSetError, UseFormSetValue } from "react-hook-form";

import { MyOrgContext } from "context/myorgcontext";
import { ExpandableSettings } from "src/domain/core/workflow";
import PowerUserNotice from "src/domain/core/workflow/components/PowerUserNotice";

import { IPInput } from "./IPInput";
import { SchedulerInput } from "./SchedulerInput";

const Row = styled("div")(() => ({
  marginBottom: "30px",
  display: "flex",
  justifyContent: "space-between",
}));

const RowCenter = styled("div")(() => ({
  textAlign: "center",

  svg: {
    width: "0.75em",
    height: "0.75em",
  },
}));

type Params = {
  head_node_instance_types: MachineInstanceType[];
  compute_node_instance_types: MachineInstanceType[];
  max_queue_size: number;
  maintain_initial_size: boolean;
  use_public_ips: boolean;
  template: Template;
};

interface AdvancedSettingsProps {
  params: Params;
  powerUser: boolean;
  actions: Actions;
  register: UseFormRegister<any>;
  formState: FormState<any>;
  setValue: UseFormSetValue<any>;
  setError: UseFormSetError<any>;
  clearErrors: UseFormClearErrors<any>;
  setUserSchedulersError: React.Dispatch<React.SetStateAction<Error>>;
  setBlueprintSchedulersError: React.Dispatch<React.SetStateAction<Error>>;
  blueprintId: number;
}

export interface Actions {
  maintain_initial_size_allowed: boolean;
}

export const AdvancedSettings: React.FC<AdvancedSettingsProps> = ({
  params,
  powerUser,
  actions,
  register,
  formState,
  setValue,
  setError,
  clearErrors,
  setUserSchedulersError,
  setBlueprintSchedulersError,
  blueprintId,
}) => {
  const myOrg = React.useContext(MyOrgContext);
  const [initializeExpanded, setInitializedExpanded] = React.useState<boolean>(false);

  return (
    <ExpandableSettings
      label={"Show advanced settings"}
      initializeAsExpanded={initializeExpanded}
      errors={
        formState.errors?.max_queue_size ||
        formState.errors?.initial_queue_size ||
        formState.errors?.maintain_initial_size ||
        formState.errors?.access_from ||
        formState.errors?.hostname ||
        formState.errors?.scheduler_id
      }
    >
      <Paper sx={{ padding: "15px" }} variant="outlined">
        {myOrg?.data.use_public_ips && (
          <IPInput
            register={register}
            formState={formState}
            setValue={setValue}
            setError={setError}
            clearErrors={clearErrors}
          />
        )}
        <SchedulerInput
          template={params.template}
          blueprintId={blueprintId}
          formState={formState}
          register={register}
          setUserSchedulersError={setUserSchedulersError}
          setBlueprintSchedulersError={setBlueprintSchedulersError}
          setInitializeExpanded={setInitializedExpanded}
        />
        <PowerUserNotice isPowerUser={powerUser}>
          <Row>
            <TextField
              id="maximum-size"
              label="Maximum Cluster Size *"
              type="number"
              sx={{ width: "49%", whiteSpace: "pre-line" }}
              disabled={!powerUser}
              inputProps={{
                "data-testid": "maximum-size",
                min: 1,
                max: params?.max_queue_size,
              }}
              helperText={
                formState.errors?.max_queue_size?.message ||
                `Maximum cluster size (e.g. 10 compute nodes)
           Range: 1 - ${params?.max_queue_size || "Loading..."}`
              }
              {...register("max_queue_size")}
              error={formState.errors?.max_queue_size && true}
            />

            <TextField
              id="initial-size"
              label="Initial Cluster Size *"
              type="number"
              sx={{ width: "49%", whiteSpace: "pre-line" }}
              disabled={!powerUser}
              inputProps={{
                "data-testid": "initial-size",
                min: 0,
                max: params?.max_queue_size,
              }}
              helperText={
                formState.errors?.initial_queue_size?.message ||
                `Intial cluster size (e.g. 2 compute nodes) 
           Range: 0 - ${params?.max_queue_size || "Loading..."}`
              }
              {...register("initial_queue_size", {
                required: true,
              })}
              error={formState.errors?.initial_queue_size && true}
            />
          </Row>

          {actions.maintain_initial_size_allowed && (
            <RowCenter>
              <FormControlLabel
                control={
                  <Switch defaultChecked={params?.maintain_initial_size} {...register("maintain_initial_size")} />
                }
                label="Maintain initial size"
                sx={{ marginRight: 0 }}
                disabled={!powerUser}
              />

              <Tooltip title="Select to ensure cluster always has at least the inital size">
                <span>
                  <IconButton size="small" disabled={!powerUser}>
                    <HelpIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </RowCenter>
          )}
        </PowerUserNotice>
      </Paper>
    </ExpandableSettings>
  );
};
